import type RegistrarAssinaturaPushDto from '@/models/dto/push/RegistrarAssinaturaPushDto';
import type RemoverAssinaturaPushDto from '@/models/dto/push/RemoverAssinaturaPushDto';
import type VapidPublicKeyDto from '@/models/dto/push/VapidPublicKeyDto';
import VapidPublicKeyResource from '@/models/resources/push/VapidPublicKeyResource';

import HttpService from './HttpService';

const URL_BASE_PUSH = '/api/push';

/**
 * Serviço de Web Push (BE-24227), hospedado fora do backend "portal" — daí a baseURL dedicada.
 * Responde JSON puro, sem o envelope `ApiResponse`.
 */
class PushSubscriptionService extends HttpService {
  private chavePublicaVapid: Promise<VapidPublicKeyResource> | null = null;

  private get configuracao() {
    return { baseURL: appSettings.VUE_APP_URL_API_PUSH };
  }

  /**
   * A chave VAPID é estática do servidor (trocá-la invalida todas as assinaturas), então é buscada
   * uma vez por carregamento da página. Uma falha não fica em cache, para o próximo gesto tentar de novo.
   */
  obterChavePublicaVapid(): Promise<VapidPublicKeyResource> {
    this.chavePublicaVapid ??= this.get<VapidPublicKeyDto>(`${URL_BASE_PUSH}/chave-vapid`, this.configuracao)
      .then(dto => new VapidPublicKeyResource(dto))
      .catch((error: unknown) => {
        this.chavePublicaVapid = null;
        throw error;
      });

    return this.chavePublicaVapid;
  }

  async registrar(assinatura: RegistrarAssinaturaPushDto): Promise<void> {
    await this.post<void>(`${URL_BASE_PUSH}/subscriptions`, assinatura, this.configuracao);
  }

  /**
   * Rota anônima: o backend ignora o `Authorization` que o `requestInterceptor` anexa, mesmo
   * expirado ou de outro usuário, e por isso a remoção funciona depois de a sessão ser limpa.
   * O `keepalive` (só existe no adapter `fetch`) deixa o pedido terminar quando o logout navega ou
   * fecha a página logo em seguida; sem ele, o aparelho seguiria recebendo push de quem saiu.
   */
  async remover(assinatura: RemoverAssinaturaPushDto): Promise<void> {
    await this.post<void>(`${URL_BASE_PUSH}/subscriptions/remocao`, assinatura, {
      ...this.configuracao,
      adapter: 'fetch',
      fetchOptions: { keepalive: true },
    });
  }
}

const pushSubscriptionService = new PushSubscriptionService();
export default pushSubscriptionService;
